<script setup lang="ts">
import { onLongPress } from "@vueuse/core";
import { ref } from "vue";

import actions from "~/actions";
import { EditorMode } from "~/shared/enums";
import type { Task } from "~/shared/types";
import { useAppStore, usePageStore, useTenantStore } from "~/stores";

import { TASK_CONTEXT_MENU_OPTIONS } from "../constants";

const props = defineProps<{
  task: Task;
  editorMode: EditorMode;
  disabled?: boolean;
  onTouchStart?: (event: MouseEvent | KeyboardEvent | TouchEvent) => void;
}>();

const appStore = useAppStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const isTaskSelected = () => {
  const isListMiniNonRoadmapMode =
    props.editorMode === EditorMode.LIST_MINI || props.editorMode === EditorMode.LIST_MINI_TCM;
  return isListMiniNonRoadmapMode
    ? (appStore
        .getSubtasksListVisualization()
        ?.getSelectedRows()
        .some((row) => row.data?.duid === props.task.duid) ?? false)
    : appStore.selectedTaskDuids.has(props.task.duid);
};

const onClick = () => {
  appStore.openTaskDetailForTask(props.task.duid);
  actions.visualization.selectRowByIdAndScroll(props.task.duid);
};

const onContextMenu = (event: MouseEvent) => {
  if (pageStore.isPublicView || (tenantStore.isDart && !pageStore.adminHidden && event.altKey)) {
    return;
  }

  event.preventDefault();
  const visualization = appStore.getActiveVisualization();
  const isSubtasksContext = visualization !== appStore.getBaseVisualization() && !isTaskSelected();
  const tasks = isSubtasksContext ? [props.task] : undefined;

  appStore.openContextMenu(event, actions.context.task(props.task, props.editorMode, tasks), TASK_CONTEXT_MENU_OPTIONS);
};

const stopIfAlreadySelected = (event: MouseEvent) => {
  if (event.shiftKey || (pageStore.isMac ? event.metaKey : event.ctrlKey) || !isTaskSelected()) {
    return;
  }
  event.stopPropagation();
};

let longPressEvent: MouseEvent | undefined;

const onLongPressHandler = (event: MouseEvent) => {
  longPressEvent = event;
};

const onTouchEnd = (event: MouseEvent | TouchEvent) => {
  if (!longPressEvent) {
    return;
  }

  event.preventDefault();
  onContextMenu(longPressEvent);
  longPressEvent = undefined;
};

const longPressWrapper = ref<HTMLElement | null>(null);
onLongPress(longPressWrapper, onLongPressHandler);
</script>

<template>
  <slot
    v-if="editorMode === EditorMode.LIST && !pageStore.isMobile"
    v-bind="{ onclick: stopIfAlreadySelected, oncontextmenu: onContextMenu }" />

  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
  <div
    v-else
    ref="longPressWrapper"
    class="relative size-full"
   
    @click="stopIfAlreadySelected"
    @contextmenu="onContextMenu"
    @trigger="onLongPress"
    @touchstart="onTouchStart"
    @touchend="onTouchEnd">
    <slot v-bind="{ onclick: () => {}, oncontextmenu: () => {} }" />
    <div
      v-if="!disabled && pageStore.isMobile"
      class="absolute inset-0"
      @click.stop="onClick"
      @keydown.enter.stop="onClick" />
  </div>
</template>
